import uuidv4 from 'uuid/v4';
import basic from './common/basic.js';
import rect from './common/rect.js';
import updater from '../updater.js';
import { roundToNearestMultiple } from '../../../utils.js';

/* State example:
state: {
  byId: {
    "jumper_1": {
      "x": 10205,
      "y": 10469,
      "id": "elem_1",
      "elementId": "65d50f81-454b-452c-9a01-9ffabccff17b",
    },
    "jumper_2": {
      "x": 10425,
      "y": 10293,
      "id": "elem_2",
      "elementId": "65d50f81-454b-452c-9a01-9ffabccff17b",
    },
  }
}
*/
const NEW_JUMPER = {
  elementId: null,
};

const options = {
  container: { type: 'boards', prop: 'jumpers' },
};

const state = { byId: {} };

const getters = {
  ...basic.getters(options),
  ...rect.getters(options),

  /**
   * Return an array with the ids of the jumpers targeting an element
   * @param   {String} id
   * @returns {Array}             The jumper ids
   */
  getElementJumpers(state) {
    return id => {
      // Filter only the jumpers that have this elementId
      return Object.values(state.byId)
        .filter(jumper => id === jumper.elementId)
        .map(j => j.id);
    };
  },
};

const actions = {
  ...basic.actions({
    type: 'jumpers',
    container: { type: 'boards', prop: 'jumpers' },
  }),

  /**
   * Create a new default jumper
   * @param {Object}  boardId   The board into which that will be created
   * @param {String}  id        The new jumper id
   * @param {Object}  position  The position onto the board
   * @param {Object}  props     Customization to the default values
   * @param {Boolean} apply     Whether to apply the patch after finishing
   */
  customCreate(context, { boardId, id = null, position, props = {}, apply }) {
    if (id === null) {
      id = uuidv4();
    }
    let x = position.x - 200 / 2;
    let y = position.y - 81 / 2;

    // Constrain the jumper position to never leave the board limits
    x = Math.max(x, 0);
    y = Math.max(y, 0);
    x = Math.min(x, 20000 - 200);
    y = Math.min(y, 20000 - 81);

    const { snap, size } = context.rootGetters['project/config/getGrid'];
    if (snap) {
      x = roundToNearestMultiple(x, size);
      y = roundToNearestMultiple(y, size);
    }
    const jumper = { id, ...NEW_JUMPER, x, y, ...props };
    context.dispatch('create', {
      node: jumper,
      tId: boardId,
      tIndex: '-',
      apply: false,
    });
    updater.complete({ apply });
    return Promise.resolve(id);
  },

  /**
   * Update some of the jumper's properties (jumper id should be filled)
   * @param {String}  id    The jumper id
   * @param {Object}  props Contains only the properties that are changed
   * @param {Boolean} apply Whether to apply the patch after finishing
   */
  customUpdate(context, { id, props, apply }) {
    context.dispatch('update', { id, props, apply: false });
    updater.complete({ apply });
  },

  /**
   * Remove a jumper with specified id from the store
   * @param {String}  id      The jumper id
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  customDelete(context, { id, apply }) {
    // Delete the jumper
    context.dispatch('delete', { id, apply: false });
    // Delete its connections
    context.dispatch(
      'project/data/connections/deleteConnections',
      { id, apply: false },
      { root: true }
    );
    // Deselect it
    context.dispatch(
      'project/config/jumpers/deselect',
      { id, apply: false },
      { root: true }
    );
    updater.complete({ apply });
  },

  /**
   * Clear all jumpers targeting a specific element
   * @param {String}  id      The element id
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  clearElementJumpers(context, { id, apply }) {
    const jumpers = context.getters.getElementJumpers(id);
    jumpers.forEach(jid => {
      context.dispatch('update', {
        id: jid,
        props: { elementId: null },
        apply: false,
      });
    });
    updater.complete({ apply });
  },
};

const mutations = {};

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default module;
